import { motion } from 'framer-motion';
import type { FunctionComponent, ReactNode } from 'react';
import { memo } from 'react';

import { useChatComposerActive, useChatComposerCallbacks } from './components/ChatComposerContext';
import { COMPOSER_TRANSITION } from './constants';

const TILE_RECEDED_SCALE = 0.99;
const TILE_RECEDED_MARGIN_INLINE = `-${(((1 - TILE_RECEDED_SCALE) / (2 * TILE_RECEDED_SCALE)) * 100).toFixed(4)}%`;
const TILE_CHAT_SPACER_HEIGHT_PX = 54;
const TILE_SCALE_TRANSITION = 'transform 200ms ease-out, margin 200ms ease-out';

export const TileChatFrame: FunctionComponent<{ readonly children: ReactNode }> = ({ children }) => {
    const { isFloatingComposerVisible } = useChatComposerCallbacks();
    const { isContentUnderChat } = useChatComposerActive();

    return (
        <div
            className="relative flex h-full min-h-0 flex-col"
            data-element="tile-chat-frame"
            data-state={isContentUnderChat ? 'receded' : 'idle'}
            style={{
                marginInline: isContentUnderChat ? TILE_RECEDED_MARGIN_INLINE : undefined,
                transform: isContentUnderChat ? `scale(${TILE_RECEDED_SCALE})` : undefined,
                transformOrigin: 'center center',
                transition: TILE_SCALE_TRANSITION,
            }}
        >
            <div className="flex min-h-0 flex-1 flex-col">{children}</div>
            <TileChatSpacer isFloatingComposerVisible={isFloatingComposerVisible} />
        </div>
    );
};

const TileChatSpacer: FunctionComponent<{ readonly isFloatingComposerVisible: boolean }> = memo(
    ({ isFloatingComposerVisible }) => {
        const { isExpanded } = useChatComposerActive();
        const { isComposerReady } = useChatComposerCallbacks();
        const isSpaceReserved = isFloatingComposerVisible && isComposerReady && !isExpanded;

        return (
            <motion.div
                aria-hidden={true}
                className="shrink-0"
                data-element="tile-chat-spacer"
                data-state={isSpaceReserved ? 'reserved' : 'released'}
                animate={{ height: isSpaceReserved ? TILE_CHAT_SPACER_HEIGHT_PX : 0 }}
                initial={false}
                transition={COMPOSER_TRANSITION}
            />
        );
    },
);
